import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { STATUS_LABEL } from "@/lib/constants";
import { toast } from "sonner";
import { Loader2, Save, Users, WifiOff, Clock, RefreshCw, CloudUpload } from "lucide-react";
import {
  useOfflineQueue, enqueueGrade, flushQueue, isNetworkError, cacheGradeRoster, readGradeRoster,
} from "@/lib/offlineQueue";

// Quick direct grade entry: teacher types a score per student for an assignment (works offline).
export default function GradeRosterDialog({ open, onOpenChange, assignment, onSaved }) {
  const [roster, setRoster] = useState([]);
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const { gradeQueue, online, syncing } = useOfflineQueue();

  const pending = gradeQueue.filter((q) => q.assignment_id === assignment?.id);

  const load = () => {
    if (!assignment) return;
    setLoading(true);
    setFromCache(false);
    api.get(`/assignments/${assignment.id}/roster`).then((r) => {
      cacheGradeRoster(assignment.id, r.data);
      apply(r.data);
    }).catch(() => {
      const c = readGradeRoster(assignment.id);
      if (c) { apply(c); setFromCache(true); }
      else toast.error("Tidak ada koneksi dan belum ada data tersimpan untuk tugas ini");
    }).finally(() => setLoading(false));
  };

  const apply = (data) => {
    const q = gradeQueue.filter((x) => x.assignment_id === assignment.id);
    const merged = data.map((x) => {
      const p = q.find((y) => y.student_id === x.student_id);
      return p ? { ...x, status: "dinilai", final_score: p.final_score } : x;
    });
    setRoster(merged);
    const m = {}; merged.forEach((x) => { if (x.final_score != null) m[x.student_id] = x.final_score; });
    setScores(m);
  };

  useEffect(() => { if (open && assignment) load(); }, [open, assignment]); // eslint-disable-line react-hooks/exhaustive-deps

  const sync = async () => {
    const r = await flushQueue();
    if (r.grades) { toast.success(`${r.grades} nilai offline berhasil dikirim`); load(); onSaved?.(); }
    if (r.gradesFailed) toast.warning(`${r.gradesFailed} nilai masih menunggu koneksi`);
  };
  useEffect(() => {
    if (!open || !online) return;
    if (pending.length) sync(); else if (fromCache) load();
  }, [online]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveOne = async (studentId) => {
    const val = scores[studentId];
    if (val === "" || val == null) { toast.error("Isi nilai dulu"); return; }
    const score = parseFloat(val);
    if (Number.isNaN(score) || score < 0 || score > 100) { toast.error("Nilai harus 0–100"); return; }
    const st = roster.find((x) => x.student_id === studentId);
    const markLocal = () => setRoster((r) => r.map((x) => x.student_id === studentId ? { ...x, status: "dinilai", final_score: score } : x));
    const saveOffline = () => {
      enqueueGrade({ assignment_id: assignment.id, assignment_title: assignment.title, student_id: studentId, student_name: st?.name, final_score: score, teacher_feedback: "" });
      markLocal();
      toast.info(`Sinyal hilang — nilai ${st?.name || ""} disimpan offline, akan dikirim otomatis`, { duration: 4000 });
    };
    setSavingId(studentId);
    if (!navigator.onLine) { saveOffline(); setSavingId(null); return; }
    try {
      await api.post("/submissions/direct", { assignment_id: assignment.id, student_id: studentId, final_score: score, teacher_feedback: "" });
      toast.success("Nilai tersimpan");
      markLocal();
      onSaved?.();
    } catch (e) {
      if (isNetworkError(e)) saveOffline(); else toast.error(e.response?.data?.detail || "Gagal menyimpan nilai");
    } finally { setSavingId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Users className="w-5 h-5 text-teal-600" /> Input Nilai · {assignment?.title}</DialogTitle></DialogHeader>

        {(!online || pending.length > 0 || fromCache) && (
          <div className={`rounded-xl p-3 flex items-center gap-2 text-xs ${online ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-slate-800 text-white"}`} data-testid="grade-offline-bar">
            {online ? <CloudUpload className="w-4 h-4 shrink-0" /> : <WifiOff className="w-4 h-4 shrink-0" />}
            <span className="flex-1">
              {!online && <>Mode offline — nilai disimpan di perangkat dan dikirim otomatis saat sinyal kembali.{pending.length > 0 && ` ${pending.length} tersimpan.`}</>}
              {online && pending.length > 0 && <span data-testid="grade-pending-count">{pending.length} nilai offline menunggu dikirim</span>}
              {online && pending.length === 0 && fromCache && "Menampilkan data tersimpan."}
            </span>
            {online && pending.length > 0 && (
              <Button size="sm" onClick={sync} disabled={syncing} className="rounded-lg h-8 bg-amber-600 hover:bg-amber-700 text-white" data-testid="grade-sync-btn">
                {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />} Kirim
              </Button>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>
        ) : roster.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">Belum ada murid di kelas ini.</p>
        ) : (
          <div className="space-y-2">
            {roster.map((s) => {
              const st = STATUS_LABEL[s.status] || STATUS_LABEL.belum;
              const isPending = pending.some((q) => q.student_id === s.student_id);
              return (
                <div key={s.student_id} className="flex items-center gap-3 bg-slate-50 rounded-xl p-3" data-testid={`roster-row-${s.student_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{s.name}{s.rombel && <span className="text-[10px] text-slate-400 font-normal"> · {s.rombel}</span>}</p>
                    <div className="flex items-center gap-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
                      {isPending && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid={`grade-pending-${s.student_id}`}><Clock className="w-3 h-3" /> Menunggu sinyal</span>}
                    </div>
                  </div>
                  <Input type="number" min="0" max="100" value={scores[s.student_id] ?? ""} onChange={(e) => setScores({ ...scores, [s.student_id]: e.target.value })} className="w-20 rounded-lg h-10 text-center" placeholder="0-100" data-testid={`roster-score-${s.student_id}`} />
                  <Button size="sm" className="rounded-lg bg-teal-600 hover:bg-teal-700" onClick={() => saveOne(s.student_id)} disabled={savingId === s.student_id} data-testid={`roster-save-${s.student_id}`}>
                    {savingId === s.student_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
